import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer, Package2, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

export default function OndaSeparacaoPage() {
  const nav = useNavigate()
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [gerando, setGerando] = useState(false)

  const { data: pedidos = [], isLoading } = useQuery<any[]>({
    queryKey: ['pedidos-aprovados'],
    queryFn: async () => {
      const all = (await api.get('/vendas')).data
      // Bloqueia pedidos com decisão de qualidade pendente — não podem separar
      // até vendedor resolver (ver tela /vendas/decisoes-qualidade)
      return all.filter((p: any) => p.status === 'aprovado' && !p.decisao_qualidade_pendente)
    },
  })

  // Filtros inteligentes
  const [busca, setBusca] = useState('')
  const [filtroRota, setFiltroRota] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('')

  const rotasDisponiveis = useMemo(
    () => Array.from(new Set(pedidos.map(p => p.rota_nome).filter(Boolean))).sort() as string[],
    [pedidos])

  const pedidosFiltrados = useMemo(() => pedidos.filter(p => {
    if (filtroRota && p.rota_nome !== filtroRota) return false
    if (filtroTipo && p.tipo_venda !== filtroTipo) return false
    if (busca) {
      const q = busca.toLowerCase().trim()
      const hay = [`#${p.numero}`, String(p.numero), p.cliente_nome, p.cliente_codigo, p.cliente_documento]
        .filter(Boolean).join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  }), [pedidos, busca, filtroRota, filtroTipo])

  function toggle(id: number) {
    const s = new Set(selecionados)
    s.has(id) ? s.delete(id) : s.add(id)
    setSelecionados(s)
  }

  // Selecionar/limpar opera sobre a lista FILTRADA (o que está à vista)
  const idsVisiveis = pedidosFiltrados.map(p => p.id)
  const todosVisiveisSelecionados = idsVisiveis.length > 0 && idsVisiveis.every(id => selecionados.has(id))
  function toggleAll() {
    setSelecionados(prev => {
      const n = new Set(prev)
      if (todosVisiveisSelecionados) idsVisiveis.forEach(id => n.delete(id))
      else idsVisiveis.forEach(id => n.add(id))
      return n
    })
  }

  const totais = useMemo(() => {
    const sel = pedidos.filter(p => selecionados.has(p.id))
    return {
      qtd: sel.length,
      itens: sel.reduce((acc, p) => acc + (p.itens?.length || 0), 0),
      valor: sel.reduce((acc, p) => acc + Number(p.total), 0),
    }
  }, [pedidos, selecionados])

  async function iniciarOnda() {
    if (selecionados.size === 0) { toast.error('Selecione ao menos 1 pedido'); return }
    setGerando(true)
    try {
      const r = await api.post('/vendas/cargas/iniciar', {
        pedido_ids: Array.from(selecionados),
        descricao: `Onda ${new Date().toLocaleDateString('pt-BR')}`,
      })
      toast.success(`Onda #${r.data.numero} criada!`)
      nav(`/vendas/cargas/${r.data.id}/separacao`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao iniciar onda')
    } finally {
      setGerando(false)
    }
  }

  async function gerarMapa() {
    if (selecionados.size === 0) {
      toast.error('Selecione pelo menos 1 pedido')
      return
    }
    setGerando(true)
    try {
      const token = localStorage.getItem('pain_token')
      const r = await fetch('/api/vendas/pedidos/onda/mapa-consolidado', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pedido_ids: Array.from(selecionados) }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
      toast.success(`Mapa gerado com ${selecionados.size} pedidos`)
    } catch (e: any) {
      toast.error(`Falha: ${e.message}`)
    } finally {
      setGerando(false)
    }
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={() => nav('/vendas')} className="p-2 rounded hover:bg-ink-100">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Nova onda de separação</h1>
          <p className="text-sm text-ink-500">Selecione os pedidos aprovados para gerar um mapa consolidado.</p>
        </div>
      </div>

      <div className="sticky top-4 z-10 bg-white rounded-xl shadow-lg border border-ink-200 p-3 flex items-center justify-between">
        <div className="text-sm">
          <strong>{totais.qtd}</strong> pedido(s) selecionado(s) · <strong>{totais.itens}</strong> itens ·
          <strong className="text-brand-600 ml-1">{formatMoney(totais.valor)}</strong>
        </div>
        <div className="flex gap-2">
          <button onClick={gerarMapa}
            disabled={gerando || selecionados.size === 0}
            className="bg-slate-600 hover:bg-slate-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2.5 rounded-lg flex items-center gap-2">
            <Printer size={14} /> Só imprimir mapa
          </button>
          <button onClick={iniciarOnda}
            disabled={gerando || selecionados.size === 0}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-lg flex items-center gap-2">
            🌊 Iniciar onda
          </button>
        </div>
      </div>

      {/* Filtros inteligentes */}
      {pedidos.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-8" placeholder="Buscar por # ou cliente (nome/código)..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={enterAbrePrimeiro(pedidosFiltrados, p => toggle(p.id))} />
          </div>
          <select className="input w-48" value={filtroRota} onChange={e => setFiltroRota(e.target.value)}>
            <option value="">Todas as rotas</option>
            {rotasDisponiveis.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="input w-44" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="">Com e sem nota</option>
            <option value="fiscal">Com nota (NF-e)</option>
            <option value="simples">Sem nota</option>
          </select>
          <span className="text-xs text-ink-500">{pedidosFiltrados.length} de {pedidos.length}</span>
        </div>
      )}

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center text-ink-400"><Loader2 className="animate-spin inline mr-2" /> Carregando...</div>
        ) : pedidosFiltrados.length === 0 ? (
          <div className="p-10 text-center text-ink-400 text-sm">
            <Package2 size={32} className="mx-auto mb-2 opacity-40" />
            {pedidos.length === 0
              ? 'Nenhum pedido aprovado aguardando separação.'
              : 'Nenhum pedido nos filtros aplicados.'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input type="checkbox"
                    checked={todosVisiveisSelecionados}
                    onChange={toggleAll} />
                </th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">#</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Cliente</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Rota</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Itens</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {pedidosFiltrados.map(p => (
                <tr key={p.id}
                  onClick={() => toggle(p.id)}
                  className={`cursor-pointer hover:bg-ink-50 ${selecionados.has(p.id) ? 'bg-indigo-50/50' : ''}`}>
                  <td className="px-3 py-2.5">
                    <input type="checkbox" checked={selecionados.has(p.id)}
                      onChange={() => toggle(p.id)} onClick={e => e.stopPropagation()} />
                  </td>
                  <td className="px-3 py-2.5 font-medium text-sm">#{p.numero}</td>
                  <td className="px-3 py-2.5 text-sm">
                    {p.cliente_codigo && <span className="text-ink-400 font-mono text-xs mr-1">{p.cliente_codigo}</span>}
                    {p.cliente_nome || `#${p.cliente_id}`}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-ink-600">{p.rota_nome || '—'}</td>
                  <td className="px-3 py-2.5 text-center text-sm">{p.itens?.length || 0}</td>
                  <td className="px-3 py-2.5 text-right text-sm font-medium">{formatMoney(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
